import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Box, Container, ToggleButtonGroup, ToggleButton, CircularProgress, Typography, IconButton, Drawer } from '@mui/material';
import axios from 'axios';
import { FilterList } from '@mui/icons-material';
import { primaryColor } from '../theme';
import '../App.css';
import moment from 'moment';
import renderContent from '../components/renderContent';

const BranchManagerDashboard = () => {
  const navigate = useNavigate();
  const [contentData, setContentData] = useState([]);
  const [filteredContentData, setFilteredContentData] = useState([]);
  const [isFilterOpen, setIsFilterOpen] = useState(false);
  const [loading, setLoading] = useState(true);
  const [period, setPeriod] = useState('all');
  const [platform, setPlatform] = useState('all');
  const [valueChain, setValueChain] = useState('all');
  const [targetUser, setTargetUser] = useState('all');
  const [uniquePeriods, setUniquePeriods] = useState([]);
  const [uniquePlatforms, setUniquePlatforms] = useState([]);
  const [uniqueValueChains, setUniqueValueChains] = useState([]);
  const [uniqueTargetUsers, setUniqueTargetUsers] = useState([]);
  const [userId, setUserId] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = 6;

  useEffect(() => {
    const user = JSON.parse(localStorage.getItem('user'));
    if (user) {
      setUserId(user.id);
    }

    const fetchContent = async () => {
      try {
        const response = await axios.get(`${process.env.REACT_APP_API_BASE_URL}/content/branch`);
        setContentData(response.data);
        setFilteredContentData(response.data);
        setUniquePeriods(getUniquePeriods(response.data));
        setUniquePlatforms(getUniquePlatforms(response.data));
        setUniqueValueChains(getUniqueValueChains(response.data));
        setUniqueTargetUsers(getUniqueTargetUsers(response.data));
        setLoading(false);
      } catch (error) {
        console.error('Error fetching content:', error);
        setLoading(false);
      }
    };

    fetchContent();
  }, []);

  useEffect(() => {
    filterContent();
  }, [period, platform, valueChain, targetUser, contentData]);

  const getUniquePeriods = (data) => {
    const periods = data.map(content => moment(content.dateUploaded).format('YYYY-MM'));
    return ['all', ...new Set(periods)];
  };

  const getUniquePlatforms = (data) => {
    const platforms = data.map(content => content.platform);
    return ['all', ...new Set(platforms)];
  };

  const getUniqueValueChains = (data) => {
    const valueChains = data.map(content => content.valueChain);
    return ['all', ...new Set(valueChains)];
  };

  const getUniqueTargetUsers = (data) => {
    const targetUsers = data.map(content => content.targetUser);
    return ['all', ...new Set(targetUsers)];
  };

  const handlePeriodChange = (event) => {
    setPeriod(event.target.value);
  };

  const handlePlatformChange = (event) => {
    setPlatform(event.target.value);
  };

  const handleValueChainChange = (event) => {
    setValueChain(event.target.value);
  };

  const handleTargetUserChange = (event) => {
    setTargetUser(event.target.value);
  };

  const filterContent = () => {
    let filtered = contentData;

    // Filter by period
    if (period !== 'all') {
      filtered = filtered.filter(content => moment(content.dateUploaded).format('YYYY-MM') === period);
    }

    // Filter by platform
    if (platform !== 'all') {
      filtered = filtered.filter(content => content.platform === platform);
    }

    // Filter by valueChain
    if (valueChain !== 'all') {
      filtered = filtered.filter(content => content.valueChain === valueChain);
    }

    // Filter by targetUser
    if (targetUser !== 'all') {
      filtered = filtered.filter(content => content.targetUser === targetUser);
    }

    setFilteredContentData(filtered);
  };

  const handleViewMore = async (id) => {
    try {
      navigate(`/branch/content/${id}`);
      await axios.post(`${process.env.REACT_APP_API_BASE_URL}/content/${id}/view`, { userId });
    } catch (error) {
      console.error('Error tracking view:', error);
    }
  };

  const handleShare = async (platform, content, topic, id) => {
    try {
      await axios.post(`${process.env.REACT_APP_API_BASE_URL}/content/${id}/view`, { userId });
    } catch (error) {
      console.error('Error tracking view:', error);
    }
    let shareUrl = '';
    // Create a temporary DOM element to extract the text content
    const tempElement = document.createElement('div');
    tempElement.innerHTML = content;
    const textContent = tempElement.innerText;
    const formattedText = `${topic}\n${textContent}`;
    const encodedContent = encodeURIComponent(formattedText);
    // const encodedContentUrl = encodeURIComponent(`https://equity.digicow.co.ke/public/content/${id}`);

    switch (platform) {
      case 'Whatsapp':
        shareUrl = `https://api.whatsapp.com/send?text=${encodedContent}`;
        break;
      case 'Facebook':
        shareUrl = `https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(`https://equity.digicow.co.ke/api/post/${id}`)}`;
        break;
      default:
        break;
    }

    window.open(shareUrl, '_blank', 'noopener,noreferrer');
  };

  const handleCopy = async (content, topic, id) => {
    // Create a temporary DOM element to extract the text content
    try {
      await axios.post(`${process.env.REACT_APP_API_BASE_URL}/content/${id}/view`, { userId });
    } catch (error) {
      console.error('Error tracking view:', error);
    }
    const tempElement = document.createElement('div');
    tempElement.innerHTML = content;
    const textContent = tempElement.innerText;
    const formattedText = `${topic}\n${textContent}`;
    navigator.clipboard.writeText(formattedText);
    alert('Content copied to clipboard');
  };

  const handlePageChange = (category, value) => {
    setCurrentPage(prevState => ({ ...prevState, [category]: value }));
  };

  const toggleFilterDrawer = () => {
    setIsFilterOpen(!isFilterOpen);
  };

  const renderPaginatedContent = (category, items) => {
    const currentCategoryPage = currentPage[category] || 1;
    return renderContent(category, items, handleViewMore, handleShare, handleCopy, currentCategoryPage, (event, value) => handlePageChange(category, value), itemsPerPage);
  };

  if (!contentData) {
    return (
      <Container maxWidth="md" sx={{ mt: 4 }}>
        <Typography variant="h6" color="error">Approved content not available yet!</Typography>
      </Container>
    );
  }

  if (loading) {
    return <CircularProgress />;
  }

  const groupedContent = {
    Presentations: filteredContentData.filter(item => item.platform === 'Presentations'),
    Written: filteredContentData.filter(item => item.platform === 'Written'),
    Video: filteredContentData.filter(item => item.platform === 'Video'),
    Audio: filteredContentData.filter(item => item.platform === 'Audio'),
    SMS: filteredContentData.filter(item => item.platform === 'SMS'),
    Whatsapp: filteredContentData.filter(item => item.platform === 'Whatsapp'),
    Facebook: filteredContentData.filter(item => item.platform === 'Facebook')
  };

  const filterPane = (
    <Box sx={{ display: { xs: 'block', md: 'block' }, width: '100%', mr: 4 }}>
        <Typography variant="h6" gutterBottom style={{ color: primaryColor }}>
          Filter By
        </Typography>
        <Box mb={1}>
          <Typography variant="subtitle1" style={{ backgroundColor: primaryColor, color: 'white', padding: '8px', borderRadius: '4px' }}>
            Period
          </Typography>
          <ToggleButtonGroup
            value={period}
            onChange={handlePeriodChange}
            aria-label="period"
            orientation="vertical"
            fullWidth
          >
            {uniquePeriods.map((periodOption, index) => (
            <ToggleButton key={index} value={periodOption} aria-label="last week" sx={{ borderColor: primaryColor, color: primaryColor }}>
              {periodOption === 'all' ? 'All' : moment(periodOption, 'YYYY-MM').format('MMMM YYYY')}
            </ToggleButton>
            ))}
          </ToggleButtonGroup>
        </Box>
        <Box mb={1}>
          <Typography variant="subtitle1" style={{ backgroundColor: primaryColor, color: 'white', padding: '8px', borderRadius: '4px' }}>
            Target User
          </Typography>
          <ToggleButtonGroup
            value={targetUser}
            onChange={handleTargetUserChange}
            aria-label="Target User"
            orientation="vertical"
            fullWidth
          >
            {uniqueTargetUsers.map((targetUserOption, index) => (
            <ToggleButton key={index} value={targetUserOption} aria-label="whatsapp" sx={{ borderColor: primaryColor, color: primaryColor }}>
              {targetUserOption === 'all' ? 'All' : targetUserOption}
            </ToggleButton>
            ))}
          </ToggleButtonGroup>
        </Box>
        <Box mb={1}>
          <Typography variant="subtitle1" style={{ backgroundColor: primaryColor, color: 'white', padding: '8px', borderRadius: '4px' }}>
            Platform
          </Typography>
          <ToggleButtonGroup
            value={platform}
            onChange={handlePlatformChange}
            aria-label="platform"
            orientation="vertical"
            fullWidth
          >
            {uniquePlatforms.map((platformOption, index) => (
            <ToggleButton key={index} value={platformOption} aria-label="whatsapp" sx={{ borderColor: primaryColor, color: primaryColor }}>
              {platformOption === 'all' ? 'All' : platformOption}
            </ToggleButton>
            ))}
          </ToggleButtonGroup>
        </Box>
        <Box mb={1}>
          <Typography variant="subtitle1" style={{ backgroundColor: primaryColor, color: 'white', padding: '8px', borderRadius: '4px' }}>
            Value Chain
          </Typography>
          <ToggleButtonGroup
            value={valueChain}
            onChange={handleValueChainChange}
            aria-label="value chain"
            orientation="vertical"
            fullWidth
          >
            {uniqueValueChains.map((valueChainOption, index) => (
            <ToggleButton key={index} value={valueChainOption} aria-label="maize" sx={{ borderColor: primaryColor, color: primaryColor }}>
              {valueChainOption === 'all' ? 'All' : valueChainOption}
            </ToggleButton>
            ))}
          </ToggleButtonGroup>
        </Box>
    </Box>
  );
  return (
    <Container maxWidth="false" sx={{ display: 'flex', mt: 4 }}>
      <Box sx={{ display: { xs: 'none', md: 'block' }, width: '20%', mr: 4 }}>
        <Typography variant="h6" gutterBottom style={{ color: primaryColor }}>
          Filter By
        </Typography>
        <Box mb={1}>
          <Typography variant="subtitle1" style={{ backgroundColor: primaryColor, color: 'white', padding: '8px', borderRadius: '4px' }}>
            Period
          </Typography>
          <ToggleButtonGroup
            value={period}
            onChange={handlePeriodChange}
            aria-label="period"
            orientation="vertical"
            fullWidth
          >
            {uniquePeriods.map((periodOption, index) => (
            <ToggleButton key={index} value={periodOption} aria-label="last week" sx={{ borderColor: primaryColor, color: primaryColor }}>
              {periodOption === 'all' ? 'All' : moment(periodOption, 'YYYY-MM').format('MMMM YYYY')}
            </ToggleButton>
            ))}
          </ToggleButtonGroup>
        </Box>
        <Box mb={1}>
          <Typography variant="subtitle1" style={{ backgroundColor: primaryColor, color: 'white', padding: '8px', borderRadius: '4px' }}>
            Target User
          </Typography>
          <ToggleButtonGroup
            value={targetUser}
            onChange={handleTargetUserChange}
            aria-label="Target User"
            orientation="vertical"
            fullWidth
          >
            {uniqueTargetUsers.map((targetUserOption, index) => (
            <ToggleButton key={index} value={targetUserOption} aria-label="whatsapp" sx={{ borderColor: primaryColor, color: primaryColor }}>
              {targetUserOption === 'all' ? 'All' : targetUserOption}
            </ToggleButton>
            ))}
          </ToggleButtonGroup>
        </Box>
        <Box mb={1}>
          <Typography variant="subtitle1" style={{ backgroundColor: primaryColor, color: 'white', padding: '8px', borderRadius: '4px' }}>
            Platform
          </Typography>
          <ToggleButtonGroup
            value={platform}
            onChange={handlePlatformChange}
            aria-label="platform"
            orientation="vertical"
            fullWidth
          >
            {uniquePlatforms.map((platformOption, index) => (
            <ToggleButton key={index} value={platformOption} aria-label="whatsapp" sx={{ borderColor: primaryColor, color: primaryColor }}>
              {platformOption === 'all' ? 'All' : platformOption}
            </ToggleButton>
            ))}
          </ToggleButtonGroup>
        </Box>
        <Box mb={1}>
          <Typography variant="subtitle1" style={{ backgroundColor: primaryColor, color: 'white', padding: '8px', borderRadius: '4px' }}>
            Value Chain
          </Typography>
          <ToggleButtonGroup
            value={valueChain}
            onChange={handleValueChainChange}
            aria-label="value chain"
            orientation="vertical"
            fullWidth
          >
            {uniqueValueChains.map((valueChainOption, index) => (
            <ToggleButton key={index} value={valueChainOption} aria-label="maize" sx={{ borderColor: primaryColor, color: primaryColor }}>
              {valueChainOption === 'all' ? 'All' : valueChainOption}
            </ToggleButton>
            ))}
          </ToggleButtonGroup>
        </Box>
      </Box>
      <Drawer
        anchor="left"
        open={isFilterOpen}
        onClose={toggleFilterDrawer}
        sx={{ display: { xs: 'block', md: 'none' } }}
      >
        {filterPane}
      </Drawer>
      <Box sx={{ width: { xs: '100%', md: '80%' } }}>
        <Box sx={{ display: { xs: 'block', md: 'none' }, mb: 2 }}>
          <IconButton onClick={toggleFilterDrawer} style={{ color: primaryColor }}>
          <Typography variant="body1" component="div" mr={1}>
            Filter Content
          </Typography>
          <FilterList />
          </IconButton>
        </Box>
        <Drawer
          anchor="left"
          open={isFilterOpen}
          onClose={toggleFilterDrawer}
          sx={{ display: { xs: 'block', md: 'none' } }}
        >
          {filterPane}
        </Drawer>
        {Object.keys(groupedContent).map((category) => (
          groupedContent[category].length > 0 && (
            <Box key={category}>
              {renderPaginatedContent(category, groupedContent[category])}
            </Box>
          )
        ))}
      </Box>
    </Container>
  );
};

export default BranchManagerDashboard;